import React, { useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import ChecklistRenderer from "./ChecklistRenderer";
import EvidenceUploader from "./EvidenceUploader";
import ProcessHistory from "./ProcessHistory";
import ProcessStatusBadge from "./ProcessStatusBadge";
import "./ProcessActionModal.css";

const buildChecklistState = (items = []) =>
  items.reduce((acc, item) => {
    acc[item.codigo] = {
      cumplido: !!item.cumplido,
      observacion: item.observacion || "",
    };
    return acc;
  }, {});

const normalizeComparable = (value) =>
  String(value ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

export const formatDateLabel = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("es-AR");
};

export const formatTimeLabel = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const resolvePriorityTone = (priority = "") => {
  const normalized = String(priority).toUpperCase();
  if (normalized.includes("CRIT")) return "critical";
  if (normalized.includes("ALTA")) return "high";
  if (normalized.includes("MEDIA")) return "medium";
  return "low";
};

export const buildAttemptLabel = (instance) => {
  const current = instance?.intentoActual;
  const max = instance?.intentoMaximo;

  if (current && max && max > 0) {
    return `Intento: ${current} de ${max}`;
  }

  if (current) {
    return `Intento: ${current}`;
  }

  if (instance?.intentoLabel) {
    return instance.intentoLabel;
  }

  return "";
};

function SummaryMeta({ label, value }) {
  if (!value) return null;
  return (
    <span className="process-action-modal-v2__summary-meta">
      <strong>{label}:</strong> {value}
    </span>
  );
}

function ExistingEvidenceList({ items = [] }) {
  if (!items.length) {
    return (
      <div className="process-action-modal-v2__existing-evidence-empty">
        No hay evidencias persistidas todavía.
      </div>
    );
  }

  return (
    <div className="process-action-modal-v2__existing-evidence-list">
      {items.map((item) => (
        <div key={item.id} className="process-action-modal-v2__existing-evidence-row">
          <div className="process-action-modal-v2__existing-evidence-main">
            <div className="process-action-modal-v2__existing-evidence-name">
              {item.nombreArchivo || "evidencia"}
            </div>
            <div className="process-action-modal-v2__existing-evidence-type">
              {item.tipo || item.mimeType || "Archivo"}
            </div>
          </div>
          <a
            className="process-action-modal-v2__ghost-link"
            href={`/api/crm-postventa-v2/evidencias/${item.id}/descargar`}
          >
            Descargar
          </a>
        </div>
      ))}
    </div>
  );
}

export default function ProcessActionModal({
  show,
  onHide,
  detail,
  existingEvidences = [],
  submitting = false,
  initialResult = "",
  onSubmit,
}) {
  const instance = detail?.instancia;
  const isReadOnly = !!instance?.esSoloLectura || instance?.puedeEjecutar === false;
  const [resultado, setResultado] = useState("");
  const [observacion, setObservacion] = useState("");
  const [duracionSegundos, setDuracionSegundos] = useState("");
  const [numeroContactado, setNumeroContactado] = useState("");
  const [esperaMinimaCumplida, setEsperaMinimaCumplida] = useState(false);
  const [permitirAntesDeFecha, setPermitirAntesDeFecha] = useState(false);
  const [files, setFiles] = useState([]);
  const [checklistValues, setChecklistValues] = useState({});

  React.useEffect(() => {
    if (!instance) return;
    setResultado(initialResult || instance.resultadosPermitidos?.[0] || "");
    setObservacion("");
    setDuracionSegundos("");
    setNumeroContactado("");
    setEsperaMinimaCumplida(false);
    setPermitirAntesDeFecha(false);
    setFiles([]);
    setChecklistValues(buildChecklistState(instance.checklist || []));
  }, [initialResult, instance?.id]);

  const checklistPayload = useMemo(
    () =>
      Object.entries(checklistValues).map(([codigo, item]) => ({
        codigo,
        cumplido: !!item.cumplido,
        observacion: item.observacion || "",
      })),
    [checklistValues]
  );

  const priorityTone = useMemo(() => resolvePriorityTone(instance?.prioridad), [instance?.prioridad]);
  const nextDate = formatDateLabel(instance?.fechaProximaAccion);
  const nextTime = formatTimeLabel(instance?.fechaProximaAccion);
  const attemptLabel = buildAttemptLabel(instance);
  const onboardingChecklistBlocked = useMemo(() => {
    if (normalizeComparable(instance?.procesoCodigo) !== "ONBOARDING_V2") return false;
    if (normalizeComparable(resultado) !== "COMERCIO OPERATIVO") return false;
    const checklist = Object.values(checklistValues);
    return checklist.length > 0 && checklist.some((item) => !item?.cumplido);
  }, [checklistValues, instance?.procesoCodigo, resultado]);

  const onConfirm = () => {
    if (isReadOnly) {
      return;
    }

    onSubmit?.({
      resultado,
      observacion,
      duracionSegundos: duracionSegundos ? Number(duracionSegundos) : null,
      numeroContactado,
      esperaMinimaCumplida,
      permitirAntesDeFecha,
      checklist: checklistPayload,
      evidencias: files,
      versionToken: instance?.versionToken,
      fechaHoraEvidencia: new Date().toISOString(),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered className="process-action-modal-v2">
      <div className="process-action-modal-v2__dialog-shell">
        <div className="process-action-modal-v2__header">
          <h2 className="process-action-modal-v2__title">
            {instance?.procesoLabel || instance?.procesoCodigo || "CRM Postventa V2"}
          </h2>
          <button
            type="button"
            className="process-action-modal-v2__close"
            onClick={onHide}
            aria-label="Cerrar"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="process-action-modal-v2__body">
          {!instance ? (
            <div className="process-action-modal-v2__loading">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="process-action-modal-v2__stack">
              <section className="process-action-modal-v2__summary-card">
                <div className="process-action-modal-v2__summary-block process-action-modal-v2__summary-block--commerce">
                  <div className="process-action-modal-v2__commerce-name">{instance.comercio || "Comercio"}</div>
                  <div className="process-action-modal-v2__summary-meta-row">
                    <SummaryMeta label="CUIT" value={instance.cuit} />
                    <SummaryMeta label="Terminal" value={instance.numeroTerminal} />
                    <SummaryMeta label="Comercio" value={instance.numeroComercio} />
                    <SummaryMeta label="Provincia" value={instance.provincia} />
                  </div>
                </div>

                <div className="process-action-modal-v2__summary-block process-action-modal-v2__summary-block--status">
                  {!!(instance.estadoLabel || instance.estadoActual) && (
                    <ProcessStatusBadge>{instance.estadoLabel || instance.estadoActual}</ProcessStatusBadge>
                  )}
                  {instance.canal || instance.canalLabel ? (
                    <div className="process-action-modal-v2__summary-copy">
                      Canal: {instance.canal || instance.canalLabel}
                    </div>
                  ) : null}
                  {attemptLabel ? (
                    <div className="process-action-modal-v2__summary-copy">{attemptLabel}</div>
                  ) : null}
                </div>

                <div className="process-action-modal-v2__summary-block process-action-modal-v2__summary-block--action">
                  <div className="process-action-modal-v2__summary-heading">
                    {instance.accionPermitidaLabel || instance.accionPermitida || "Próxima acción"}
                  </div>
                  {nextDate ? (
                    <div className="process-action-modal-v2__summary-copy">Fecha: {nextDate}</div>
                  ) : null}
                  {nextTime ? (
                    <div className="process-action-modal-v2__summary-copy">Horario: {nextTime}</div>
                  ) : null}
                </div>

                {!!instance.prioridad && (
                  <div className="process-action-modal-v2__summary-block">
                    <span
                      className={`process-action-modal-v2__priority process-action-modal-v2__priority--${priorityTone}`}
                    >
                      {instance.prioridad}
                    </span>
                  </div>
                )}

                {!!instance.responsable && (
                  <div className="process-action-modal-v2__summary-block">
                    <div className="process-action-modal-v2__summary-heading process-action-modal-v2__summary-heading--plain">
                      {instance.responsable}
                    </div>
                  </div>
                )}
              </section>

              {instance.esSoloLectura ? (
                <Alert variant="info" className="process-action-modal-v2__alert">
                  {instance.mensajeSoloLectura || "Gestión disponible en futuro módulo Compliance."}
                </Alert>
              ) : null}

              {!!instance.motivoBloqueoAccion && !instance.esSoloLectura ? (
                <Alert
                  variant={instance.puedeEjecutar ? "info" : "warning"}
                  className="process-action-modal-v2__alert"
                >
                  {instance.puedeEjecutar
                    ? "Acción habilitada."
                    : `Bloqueo actual: ${instance.motivoBloqueoAccion}`}
                </Alert>
              ) : null}

              <section className="process-action-modal-v2__fields-grid">
                <Form.Group className="process-action-modal-v2__field process-action-modal-v2__field--wide">
                  <Form.Label>Resultado</Form.Label>
                  <Form.Select
                    value={resultado}
                    onChange={(event) => setResultado(event.target.value)}
                    disabled={isReadOnly}
                  >
                    <option value="">Seleccionar</option>
                    {(instance.resultadosPermitidos || []).map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="process-action-modal-v2__field">
                  <Form.Label>N.º contactado</Form.Label>
                  <Form.Control
                    value={numeroContactado}
                    onChange={(event) => setNumeroContactado(event.target.value)}
                    disabled={isReadOnly}
                  />
                </Form.Group>

                <Form.Group className="process-action-modal-v2__field">
                  <Form.Label>Duración en segundos</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={duracionSegundos}
                    onChange={(event) => setDuracionSegundos(event.target.value)}
                    disabled={isReadOnly}
                  />
                </Form.Group>
              </section>

              <section className="process-action-modal-v2__main-grid">
                <div className="process-action-modal-v2__column">
                  <Form.Group className="process-action-modal-v2__field">
                    <Form.Label>Observación</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={observacion}
                      onChange={(event) => setObservacion(event.target.value)}
                      disabled={isReadOnly}
                      placeholder="Describí brevemente el resultado de la gestión..."
                    />
                  </Form.Group>

                  {!!existingEvidences.length && (
                    <section className="process-action-modal-v2__section">
                      <h3 className="process-action-modal-v2__section-title">Evidencias registradas</h3>
                      <ExistingEvidenceList items={existingEvidences} />
                    </section>
                  )}
                </div>

                <div className="process-action-modal-v2__column">
                  <section className="process-action-modal-v2__checks">
                    <Form.Check
                      type="checkbox"
                      className="process-action-modal-v2__checkbox"
                      label="Confirmar espera mínima cumplida"
                      checked={esperaMinimaCumplida}
                      onChange={(event) => setEsperaMinimaCumplida(event.target.checked)}
                      disabled={isReadOnly}
                    />
                    <Form.Check
                      type="checkbox"
                      className="process-action-modal-v2__checkbox"
                      label="Permitir gestión antes de la fecha programada"
                      checked={permitirAntesDeFecha}
                      onChange={(event) => setPermitirAntesDeFecha(event.target.checked)}
                      disabled={isReadOnly}
                    />
                  </section>

                  <section className="process-action-modal-v2__section">
                    <h3 className="process-action-modal-v2__section-title">Checklist</h3>
                    {onboardingChecklistBlocked ? (
                      <Alert variant="warning" className="process-action-modal-v2__alert">
                        Para cerrar Onboarding como comercio operativo, completá todo el checklist obligatorio.
                      </Alert>
                    ) : null}
                    <ChecklistRenderer
                      items={instance.checklist || []}
                      editable={!isReadOnly}
                      values={checklistValues}
                      onChange={(codigo, nextValue) =>
                        setChecklistValues((prev) => ({
                          ...prev,
                          [codigo]: nextValue,
                        }))
                      }
                    />
                  </section>

                  <EvidenceUploader files={files} onChange={setFiles} disabled={submitting || isReadOnly} />
                </div>
              </section>

              <section className="process-action-modal-v2__section">
                <h3 className="process-action-modal-v2__section-title">Historial</h3>
                <ProcessHistory rows={detail?.historial || []} />
              </section>
            </div>
          )}
        </div>

        <div className="process-action-modal-v2__footer">
          {!isReadOnly ? (
            <Button
              className="process-action-modal-v2__btn process-action-modal-v2__btn--primary"
              onClick={onConfirm}
              disabled={submitting || !resultado || !instance?.puedeEjecutar || onboardingChecklistBlocked}
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span>Registrando...</span>
                </>
              ) : (
                "Registrar"
              )}
            </Button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
